import {
  InferVoiceProviderFromVoiceId,
  VoiceAge,
  VoiceGender,
  VoiceProvider,
} from "@/enums/agencies";
import { SupportedLanguages } from "@/enums/internationalization";
import { VoiceOption } from "@/types/agencies";

export const FALLBACK_FEATURED_VOICE_COUNT = 4;

/**
 * Placeholder card when the script's voice id isn't in the org catalog
 * (e.g. assistant set a provider voice that isn't curated for this org).
 * Keeps the compact picker from looking "unselected".
 *
 * `displayName` is user-facing — callers must pass a translated label
 * (e.g. `t("pages.agent.selectedVoiceFallback")`), never hardcoded English.
 */
export function createFallbackVoiceOption(
  voiceId: string,
  voiceProvider: VoiceProvider | string | null | undefined,
  displayName: string
): VoiceOption {
  const provider =
    (voiceProvider as VoiceProvider | undefined) ||
    InferVoiceProviderFromVoiceId(voiceId);

  return {
    id: voiceId,
    name: displayName,
    provider,
    supportedLanguages: [SupportedLanguages.English],
    dateAdded: new Date(0).toISOString(),
    status: "active",
    labels: {
      isNew: false,
      age: VoiceAge.MIDDLE_AGED,
      gender: VoiceGender.FEMALE,
    },
  };
}

/**
 * Drop a confirmed-missing selected voice from picker choices so the user
 * cannot re-select the same id from a stale catalog card.
 */
export function omitUnavailableSelectedVoice<T extends {id: string}>(
  voices: readonly T[],
  selectedVoiceId: string | undefined,
  voiceUnavailable?: boolean
): T[] {
  if (!voiceUnavailable || !selectedVoiceId) {
    return voices as T[];
  }
  return voices.filter((voice) => voice.id !== selectedVoiceId);
}

/**
 * Resolve the selected voice for display: prefer the loaded catalog, then a
 * previously resolved option, then a minimal fallback so the UI can still pin it.
 */
export function resolveSelectedVoiceOption(
  selectedVoiceId: string | undefined,
  catalog: readonly VoiceOption[],
  options: {
    voiceProvider?: VoiceProvider | string | null;
    previouslyResolved?: VoiceOption | null;
    /** Translated placeholder name — required so English never leaks in. */
    fallbackDisplayName: string;
    /** A voice confirmed missing from the org is never pinned as a fallback card. */
    voiceUnavailable?: boolean;
  }
): VoiceOption | null {
  if (options.voiceUnavailable || !selectedVoiceId) return null;

  const fromCatalog = catalog.find((v) => v.id === selectedVoiceId);
  if (fromCatalog) return fromCatalog;

  if (options.previouslyResolved?.id === selectedVoiceId) {
    return options.previouslyResolved;
  }

  return createFallbackVoiceOption(
    selectedVoiceId,
    options.voiceProvider,
    options.fallbackDisplayName
  );
}

export function filterRecommendedVoices(voices: readonly VoiceOption[]): VoiceOption[] {
  return voices.filter((v) => v.labels?.gailRecommended);
}

export function filterCuratedVoices(voices: readonly VoiceOption[]): VoiceOption[] {
  return voices.filter(
    (v) =>
      v.labels?.gailRecommended ||
      v.labels?.popular ||
      v.labels?.isNew ||
      v.isOrganizationVoice
  );
}

/**
 * Explore catalog: every catalog voice not already shown in the featured or
 * org rows that speaks at least one org-enabled language. Extra languages on
 * a voice (German, Catalan, …) neither include nor exclude it — an
 * English + Spanish + Catalan voice is an English & Spanish voice to an
 * English/Spanish org. When the org has no languages configured, nothing is
 * scoped out. The selected voice is always kept so a legacy or assistant-set
 * selection stays reachable.
 */
export function buildExploreCatalog(
  catalog: readonly VoiceOption[],
  featured: readonly VoiceOption[],
  org: readonly VoiceOption[],
  orgLanguages: readonly SupportedLanguages[] = [],
  selectedVoiceId?: string
): VoiceOption[] {
  const alreadyShownIds = new Set(
    [...featured, ...org].map((voice) => voice.id)
  );
  const orgLanguageSet = new Set(orgLanguages);

  return catalog.filter((voice) => {
    if (alreadyShownIds.has(voice.id)) return false;
    if (orgLanguageSet.size === 0) return true;
    if (voice.id === selectedVoiceId) return true;
    return (voice.supportedLanguages ?? []).some((lang) => orgLanguageSet.has(lang));
  });
}

/**
 * Featured row for the compact picker: every Gail Recommended voice from the
 * admin portal (the grid wraps). If none are tagged, fall back to the first
 * few catalog voices so the picker is never empty.
 */
export function buildRecommendedFeaturedList(
  catalog: readonly VoiceOption[]
): VoiceOption[] {
  const recommended = filterRecommendedVoices(catalog);
  if (recommended.length > 0) {
    return recommended;
  }
  return catalog.slice(0, FALLBACK_FEATURED_VOICE_COUNT);
}

/**
 * On collapse, pin the current selection at slot 0 when it isn't already
 * featured. Recommended cards stay in the row — dropping one would hide a
 * Gail Recommended voice from both the compact row and Explore (Explore
 * still treats it as featured).
 */
export function promoteSelectedIntoFeatured(
  featured: readonly VoiceOption[],
  selected: VoiceOption | null
): VoiceOption[] {
  if (!selected) return [...featured];
  if (featured.some((voice) => voice.id === selected.id)) return [...featured];
  return [selected, ...featured];
}

export function buildFeaturedVoiceList(
  catalog: readonly VoiceOption[],
  selectedVoice: VoiceOption | null,
  options?: { promoteSelected?: boolean }
): VoiceOption[] {
  const featured = buildRecommendedFeaturedList(catalog);
  if (options?.promoteSelected) {
    return promoteSelectedIntoFeatured(featured, selectedVoice);
  }
  return featured;
}
